import React, { useEffect, useCallback, memo, useState } from "react";
import { useLocation } from "react-router-dom";
import Button, { ButtonThemes } from "../../../../ui-components/button/button";
import { useHistory } from "react-router";
import "./filter.scss";
import { LabelModes } from "../../../../ui-components/molecules/labeled-inputs/labeledInput";
import LabeledSelectInput from "../../../../ui-components/molecules/labeled-inputs/labeled-select-input/labeledSelectInput";
import { useDispatch, useSelector } from "react-redux";
import { XGSSelectOption } from "../../../../ui-components/xgs-select/xgsSelect";
import { fetchAllTerminals } from "../../../../slices/warehouse-inventory/warehouseInventorySlice";
import { getFreightTypes } from "../../../../app/data/common/route";
import queryString from "query-string";
import TerminalSummaryState from "slices/terminal-summary/TerminalSummaryState";
import { terminalSummarySelector } from "slices/terminal-summary/terminalSummarySlice";
import { Popover } from "ui-components/popover/popover";

type TrackingFilterProps = {
  sameRowButtons?: boolean;
  isLoading?: boolean;
  onSearch: (filters: any) => void;
  onClear?: () => void;
  terminalOptions?: {
    value: string;
    label: string;
  }[];
};

const defaultStatuses = [
  {
    label: "UNASSIGNED_PICKUP",
    value: "UNASSIGNED_PICKUP"
  },
  {
    label: "ASSIGNED_PICKUP",
    value: "ASSIGNED_PICKUP"
  },
  {
    label: "COMPLETED_PICKUP",
    value: "COMPLETED_PICKUP"
  },
  {
    label: "IN_HOUSE_FREIGHT",
    value: "IN_HOUSE_FREIGHT"
  },
  {
    label: "INBOUND_FREIGHT",
    value: "INBOUND_FREIGHT"
  }
]

export interface InboundPickupsFilters {
  o?: string | null;
  d?: number | null;
  s?: string[] | null;
  e?: string | null;
}

function parseQueryString<T>(queryStringInput: string): T {
  let parsed = queryString.parse(queryStringInput, {
    arrayFormat: "bracket",
    parseBooleans: true,
  }) as T;
  return parsed;
}

export const InboundPickupsFilter: React.FC<TrackingFilterProps> = memo(
  ({ onSearch, onClear, ...props }) => {
    const location = useLocation();
    const history = useHistory();
    const dispatch = useDispatch();

    const { search } = useLocation();
    const params = new URLSearchParams(search);

    const d = params.get("d");

    const [destinationTerminal, setDestinationTerminal] = useState<XGSSelectOption | null>(null);

    const terminalSummaryState: TerminalSummaryState = useSelector(terminalSummarySelector);

    useEffect(() => {
      if (props.terminalOptions) {
        setDestinationTerminal(props.terminalOptions.find((option) => option.value === d) || null);
      }
    }, [d, props.terminalOptions])

    const stringifyQuery = (query: Record<string, any>) => {
      return queryString.stringify(query, { arrayFormat: "bracket", skipNull: true, skipEmptyString: true });
    };

    const getFiltersFromURL = useCallback(() => {
      const queryParams = parseQueryString<InboundPickupsFilters>(location.search);
      return queryParams;
    }, [location.search]);

    const handleSearch = () => {
      const filters = getFiltersFromURL();
      onSearch(filters);
    };

    const handleFilterChange = (key: keyof InboundPickupsFilters, value: any, triggerSearch?: boolean) => {
      const filters = getFiltersFromURL();
      filters[key] = value;
      history.push({ search: stringifyQuery(filters) })
      triggerSearch && onSearch(filters);
    };

    const handleClear = () => {
      let filters = { s: defaultStatuses.map(s => s.value) }
      history.push({ search: stringifyQuery(filters) })
      onSearch(filters)
      onClear && onClear();
    };

    useEffect(() => {
      dispatch(fetchAllTerminals());
    }, [dispatch]);

    const onDestinationChanged = (_origin: XGSSelectOption | null | undefined) => {
      handleFilterChange("d", _origin?.value, true);
    };

    const onStatusChange = (v: any) => {
      let statuses = v ? v.map((option: XGSSelectOption) => option.value) : [];
      handleFilterChange("s", statuses, true);
    };
  
    let currentFilters = getFiltersFromURL();

    useEffect(() => {
      handleSearch();
      // eslint-disable-next-line
    }, [location])
  
    useEffect(() => {
      if (!currentFilters.s?.length) {
        // Set default statuses including UNASSIGNED
        handleFilterChange("s", defaultStatuses.map(s => s.value), true);
      } else {
        handleSearch();
      }
      // eslint-disable-next-line
    }, [])

    const selectedStatusesStrings = currentFilters.s;
    const allStatusOptions = getFreightTypes;
    const selectedStatusesOptions = selectedStatusesStrings?.map(status => allStatusOptions.find(option => option.value === status)).filter((v) => !!v);

    return (
      <div className="xgs-inbound-pickups-filter">
        <div className="xgs-inbound-pickups-filter__controls">
          <div className="xgs-inbound-pickups-filter__controls__item">
            <label className="xgs-inbound-pickups-filter__controls__item__label">Origin / Destination Terminal: <Popover values={<div>For Outbound/In-House:<br /> Destination Terminal<br />For Inbound:<br /> Origin Terminal</div>} /></label>
            <LabeledSelectInput
              isLoading={terminalSummaryState.terminallist.length === 0 ? true : false}
              onValueChange={(v: XGSSelectOption | null | undefined) => {
                setDestinationTerminal(v ?? null);
                if (v)
                  onDestinationChanged(v);
              }}
              value={destinationTerminal}
              options={props?.terminalOptions || []}
              labelMode={LabelModes.column}
              className="xgs-tracking-filter__input"
              isClearable
            />
          </div>
          <div className="xgs-inbound-pickups-filter__controls__large-item">
            <LabeledSelectInput
              onMultiValuesChange={(option) => onStatusChange(option)}
              value={selectedStatusesOptions}
              options={getFreightTypes}
              label="Freight Type:"
              labelMode={LabelModes.column}
              isClearable={false}
              isMulti
            />
          </div>
          <div className="xgs-inbound-pickups-filter__buttons xgs-inbound-pickups-filter__buttons--small xgs-inbound-pickups-filter__controls__item">
            <Button theme={ButtonThemes.blue} onClick={handleSearch}>
              Search
            </Button>
            <Button theme={ButtonThemes.gray} onClick={handleClear}>
              Clear Filters
            </Button>
          </div>
        </div>
      </div>
    );
  }
);
